/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton } from '@fluentui/react/lib/Button';
import { PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { ITextFieldProps } from '@fluentui/react/lib/TextField';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoCustomContent, logInputPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DemoTypePivot } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoTypePivot';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { ConversationSummarizationAspectsOptions } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/ConversationSummarizationAspectsOptions';
import { TextSummarizationAspectsOptions } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/DocumentSummarizationAspectsOptions';
import { SummarizationSentence } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/SummarizationSentence';
import { VisualizedConversationSummarizationResult } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/VisualizedConversationSummarizationResult';
import { VisualizedSummarizationResult } from 'src/languageStudio/appShell/prebuiltDemos/summarizationDemo/VisualizedSummarizationResult';
import {
    conversationSummarizationDemoSamples,
    summarizationDemoSamples
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import { summarizationDemoSupportedRegions } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportedRegion';
import {
    summarizationDocLink,
    summarizationOverviewLink,
    summarizationSamplesLink,
    summarizationSdkLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { supportedNumberOfSentences } from 'src/languageStudio/appShell/prebuiltDemos/utils/summarizationDropDownsOptions';
import { Language, textSummarizationDemoSupportedLanguages, summarizationDemoSupportedLanguages } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { useConversationCapabilities } from 'src/languageStudio/core/hooks/useConversationCapabilities';
import { useTextAnalyticsCapabilities } from 'src/languageStudio/core/hooks/useTextAnalyticsCapabilities';
import { getConversationSummarizationPayloadTasks } from 'src/languageStudio/core/services/conversation/conversationAnalysisService';
import { Conversation, SummaryAspects } from 'src/languageStudio/core/services/conversation/conversationSwaggers';
import {
    SummarizationSwagger,
    SummarizationTask,
    SummarizationTaskType
} from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { DemoTypePivotKeys } from 'src/languageStudio/core/types';
import {
    isDocumentSwaggerRtl,
    isDocumentSwaggerWordTokenized,
    validateLanguageStudioResource
} from 'src/languageStudio/core/utils/demosUtils';
import styled from 'styled-components';
import { LuisTextField } from '../../../../core/components/LuisTextField';
import { CalloutButton, CalloutButtonProps } from '../../../../core/components/CalloutButton';
import { getStylistV2 } from '../../../../core/themes/luisStylist';
import { useResponsiveMode } from '../../../../core/hooks/useResponsiveMode';

const messages = defineMessages({
    clearText: { id: 'languageStudio.summarizationDemoPage.clearText', defaultMessage: 'Clear text box' },
    pageHeader: { id: 'languageStudio.summarizationDemoPage.pageHeader', defaultMessage: 'Summarize information' },
    ingestionClient: { id: 'languageStudio.summarizationDemoPage.ingestionClient', defaultMessage: 'Ingestion client' },
    capabilityDescription: {
        id: 'languageStudio.summarizationDemoPage.capabilityDescription',
        defaultMessage: `This prebuilt summarization API can produce a summary for a conversation or from a document. Conversation
        summarization can summarize upon aspects and document summarization has several types.`
    },
    emptyState: {
        id: 'languageStudio.summarizationDemoPage.emptyState',
        defaultMessage: `Your results will appear here once you enter or upload some text and press Run.
        The file format needs to be .txt`
    },
    conversationInputDescription: {
        id: 'languageStudio.summarizationDemoPage.conversationInputDescription',
        defaultMessage: 'Uploaded or entered text must have a conversational format with roles (required for issue and resolution aspects) or speakers (for other aspects) identified for each turn.'
    },
    formatReadMore: {
        id: 'languageStudio.summarizationDemoPage.formatReadMore',
        defaultMessage: 'Read more about formatting requirements.'
    },
    conversationInputDescription2: {
        id: 'languageStudio.summarizationDemoPage.conversationInputDescription2',
        defaultMessage: ' If you have audio-only files and need to create a transcript, you can use the'
    },
    queryTextPlaceholder: {
        id: 'languageStudio.summarizationDemoPage.queryTextPlaceholder',
        defaultMessage: 'enter a query, such as keywords, up to 100 chars'
    },
    enterQueryText: {
        id: 'languageStudio.summarizationDemoPage.enterQueryText',
        defaultMessage: 'Specify your summarization interest (preview)'
    },
    queryTextDescription: {
        id: 'languageStudio.summarizationDemoPage.queryTextDescription',
        defaultMessage: 'Tailor your summary to your specific interests by entering relevant queries or keywords'
    }
});

export type SummarizationType = 'both' | 'extractive' | 'abstractive';

const { getClassName } = getStylistV2('DemoPage');
export const labelDescriptionIconClassName = getClassName('labelDescriptionIconClassName', { fontSize: '12px' });

const StyledStack = styled(Stack)<{ isLargeOrGreater: boolean }>(props => ({
    alignItems: props.isLargeOrGreater ? 'end' : 'normal'
}));

export const SummarizationDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { csAccountStore } = rootStore.azureStore.session;

    const isValidResource = validateLanguageStudioResource({ resource: csAccountStore, localize }).isValid;
    const isAbstractiveSummarySupportedResource = validateLanguageStudioResource({
        resource: csAccountStore,
        localize,
        supportedRegions: summarizationDemoSupportedRegions
    }).isValid;
    const isAbstractiveSummaryEnabled = isAbstractiveSummarySupportedResource || !isSignedIn;

    const {
        summarization,
        getSummarization,
        tracker: documentTracker,
        getSummarizationCurlCommand,
        resetResults: textSummarizationResetResults,
        detectedLanguages: textSummarizationDetectedLanguages
    } = useTextAnalyticsCapabilities();
    const {
        conversationTracker,
        getConversationItems,
        conversationSummarization,
        getConversationCurlCommand,
        getConversationSummarization,
        resetResults: conversationSummarizationResetResults,
        detectedLanguages: conversationSummarizationDetectedLanguages
    } = useConversationCapabilities();

    const [document, setDocument] = React.useState('');
    const [sentenceCount, setSentenceCount] = React.useState<number>();
    const [query, setEnteredQuery] = React.useState('');
    const [language, setLanguage] = React.useState<Language['code']>('en');
    const [languageAutoDetection, setLanguageAutoDetection] = React.useState(false);
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);
    const [selectedPivot, setSelectedPivot] = React.useState<DemoTypePivotKeys>('document');
    const [selectedType, setSelectedType] = React.useState<SummarizationType>(isAbstractiveSummaryEnabled ? 'both' : 'extractive');
    const [selectedSummaryAspects, setSelectedSummaryAspects] = React.useState<SummaryAspects[]>(
        isSignedIn ? [] : ['chapterTitle', 'issue', 'narrative', 'resolution', 'recap', 'follow-up tasks']
    );
    const { isLargeOrGreater } = useResponsiveMode();

    let isRunDemoDisabled = !document.length;
    const isResultsLoading =
        (documentTracker.isRunning &&
            !(summarization?.extractive?.status === 'succeeded' || summarization?.abstractive?.status === 'succeeded')) ||
        conversationTracker.isRunning;

    if (selectedPivot === 'conversation') {
        isRunDemoDisabled = isRunDemoDisabled || !selectedSummaryAspects.length;
    }

    const featureName = React.useMemo(
        () => (selectedPivot === 'conversation' ? 'Summarization conversation' : 'Summarization information'),
        [selectedPivot]
    );

    React.useEffect(() => setSelectedType(isAbstractiveSummaryEnabled ? 'both' : 'extractive'), [csAccountStore]);

    const handleDocumentChange = (newDocument: string) => {
        if (Boolean(summarization)) {
            textSummarizationResetResults();
        }

        if (Boolean(conversationSummarization)) {
            conversationSummarizationResetResults();
        }

        setDocument(newDocument);
    };

    const handleDemoPivotChange = (item: PivotItem) => {
        logInputPivotClick(
            item.props.itemKey === 'conversation' ? 'Summarization conversation' : 'Summarization information',
            item.props.itemKey
        );
        const selectedPivotKey = item.props.itemKey as DemoTypePivotKeys;
        setSelectedPivot(selectedPivotKey);
        setSentenceCount(null);
        handleDocumentChange('');
        if (!isSignedIn && item.props.itemKey === 'conversation') {
            setSelectedSampleTitle(conversationSummarizationDemoSamples[0].title);
        }
    };

    const renderDescribedLabel = ({ label, descriptionIconProps }: { label: string; descriptionIconProps: CalloutButtonProps }) => (
        <Stack horizontal verticalAlign="center" tokens={{ padding: '0 0 5px 0' }}>
            <LuisText variant="medium" fontWeight={500}>
                {label}
            </LuisText>
            <CalloutButton {...descriptionIconProps} iconClassName={labelDescriptionIconClassName} />
        </Stack>
    );

    const onRenderOptions = (defaultOptions: () => React.ReactNode) => (
        <Stack tokens={{ childrenGap: 25 }}>
            <DemoTypePivot selectedKey={selectedPivot} onLinkClick={handleDemoPivotChange} />
            {defaultOptions?.()}
            {selectedPivot === 'document' && isSignedIn && (
                <StyledStack isLargeOrGreater horizontal={isLargeOrGreater} tokens={{ childrenGap: 16 }}>
                    <SummarizationSentence
                        disabled={!isValidResource}
                        selectedNumberOfSentencesKey={sentenceCount}
                        onNumberOfSentencesChange={setSentenceCount}
                        numberOfSentencesList={supportedNumberOfSentences}
                    />
                    <LuisTextField
                        required={false}
                        value={query}
                        resizable={false}
                        data-automation-id="queryInput"
                        disabled={!isValidResource}
                        placeholder={localize(messages.queryTextPlaceholder)}
                        label={localize(messages.enterQueryText)}
                        onChange={(_, enteredquery) => setEnteredQuery(enteredquery)}
                        onRenderLabel={() =>
                            renderDescribedLabel({
                                label: localize(messages.enterQueryText),
                                descriptionIconProps: {
                                    isDarkMode: false,
                                    content: localize(messages.queryTextDescription),
                                    ariaLabel: localize(messages.queryTextDescription)
                                }
                            })
                        }
                        />
                </StyledStack>
            )}
        </Stack>
    );

    const onRenderInputBoxLabel = (labelProps: ITextFieldProps, labelDefaultRenderer: (p?: ITextFieldProps) => JSX.Element) => (
        <Stack>
            {labelDefaultRenderer(labelProps)}
            <LuisText>
                {localize(messages.conversationInputDescription)}&nbsp;
                <ExternalLink href="https://aka.ms/conversation-format-requirements">{localize(messages.formatReadMore)}</ExternalLink>
                &nbsp;
                {localize(messages.conversationInputDescription2)}&nbsp;
                <ExternalLink href="https://aka.ms/callcenter-ingestion-client">{localize(messages.ingestionClient)}</ExternalLink>
            </LuisText>
            <StackItem align="end">
                <ActionButton
                    iconProps={{ iconName: 'Reset' }}
                    text={localize(messages.clearText)}
                    disabled={!document.length || !isValidResource}
                    onClick={() => {
                        if (selectedSampleTitle) {
                            setSelectedSampleTitle(null);
                        }
                        handleDocumentChange('');
                    }}
                />
            </StackItem>
        </Stack>
    );

    const getTasksArray = () => {
        const tasks: SummarizationTaskType[] = [];

        if (selectedType === 'abstractive' || selectedType === 'both') {
            tasks.push('AbstractiveSummarization');
        }

        if (selectedType === 'extractive' || selectedType === 'both') {
            tasks.push('ExtractiveSummarization');
        }

        return tasks;
    };

    const handleRunDemo = () => {
        if (!selectedSampleTitle) {
            logDemoCustomContent(featureName);
        }

        if (selectedPivot === 'conversation') {
            getConversationSummarization({
                language,
                conversation: document,
                sampleTitle: selectedSampleTitle,
                summaryAspects: selectedSummaryAspects,
                autoDetectLanguage: languageAutoDetection,
                ...(selectedSummaryAspects.includes('resolution') && { sentenceCount })
            });
        } else {
            getSummarization({
                document,
                language,
                tasks: getTasksArray(),
                sampleTitle: selectedSampleTitle,
                ...(sentenceCount && { sentenceCount }),
                ...(query && { query }),
                autoDetectLanguage: languageAutoDetection
            });
        }
    };

    const onSummarizationTypeChange = (summaryType: SummarizationType) => {
        if (Boolean(summarization)) {
            textSummarizationResetResults();
        }
        setSelectedType(summaryType);
    };

    const onSummarizationAspectsChange = (summaryAspects: SummaryAspects[]) => {
        if (Boolean(conversationSummarization)) {
            conversationSummarizationResetResults();
        }
        setSelectedSummaryAspects(summaryAspects);
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName={featureName}
                supportedPlatforms={LuisConstants.Summarization.SupportedPlatforms}
                getSdkLink={summarizationSdkLink}
                documentationLink={summarizationDocLink}
                learnMoreLink={summarizationOverviewLink}
                demoTitle={localize(messages.pageHeader)}
                gitHubSamplesLink={summarizationSamplesLink}
                demoVersion={LuisConstants.Demos.SummarizationApiVersion}
                demoDescription={localize(messages.capabilityDescription)}
            />

            <DocumentInputStep
                document={document}
                documentLimit={125000}
                featureName={featureName}
                selectedLanguage={language}
                onLanguageChange={setLanguage}
                onRenderOptions={onRenderOptions}
                acceptedResourceType="TextAnalytics"
                onDocumentChange={handleDocumentChange}
                selectedSampleTitle={selectedSampleTitle}
                supportedRegionLink={summarizationOverviewLink}
                languagesList={selectedPivot === 'conversation' ? summarizationDemoSupportedLanguages : textSummarizationDemoSupportedLanguages}
                containerStackProps={{ tokens: { maxWidth: '100%' } }}
                onLanguageAutoDetectionClick={setLanguageAutoDetection}
                onSampleClick={sample => setSelectedSampleTitle(sample?.title)}
                textFieldProps={selectedPivot === 'conversation' && isSignedIn && { onRenderLabel: onRenderInputBoxLabel }}
                samplesList={selectedPivot === 'conversation' ? conversationSummarizationDemoSamples : summarizationDemoSamples}
                isTextRTL={isDocumentSwaggerRtl(
                    textSummarizationDetectedLanguages?.documents[0].detectedLanguage.iso6391Name ??
                        conversationSummarizationDetectedLanguages?.documents[0].detectedLanguage.iso6391Name ??
                        language
                )}
                onRenderHorizontalOptions={() =>
                    selectedPivot === 'conversation' ? (
                        <ConversationSummarizationAspectsOptions
                            currentRegion={csAccountStore?.region}
                            isDisabled={!isAbstractiveSummaryEnabled}
                            selectedSummarizationType={selectedSummaryAspects}
                            onSummarizationTypeChange={onSummarizationAspectsChange}
                        />
                    ) : (
                        <TextSummarizationAspectsOptions
                            currentRegion={csAccountStore?.region}
                            selectedSummarizationType={selectedType}
                            onSummarizationTypeChange={onSummarizationTypeChange}
                            isAbstractiveSummaryEnabled={isAbstractiveSummaryEnabled}
                        />
                    )
                }>
                <DemoPageAgreement
                    featureName={featureName}
                    onRunDemoClick={handleRunDemo}
                    isRunDisabled={isRunDemoDisabled}
                    acceptedResourceType="TextAnalytics"
                    pricingLink={textAnalyticsPricingLink}
                />
            </DocumentInputStep>

            <ResultsStep<SummarizationTask<SummarizationSwagger>[] | Conversation>
                featureName={featureName}
                isLoading={isResultsLoading}
                emptyStateMessage={localize(messages.emptyState)}
                results={selectedPivot === 'document' ? summarization?.summarizationDemoOutput : conversationSummarization}>
                {selectedPivot === 'document' ? (
                    <VisualizedSummarizationResult
                        document={document}
                        summarizationResult={summarization}
                        isLoading={documentTracker.isRunning}
                        detectedLanguage={textSummarizationDetectedLanguages}
                        isLabelerRTL={isDocumentSwaggerRtl(
                            textSummarizationDetectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                        )}
                        isLabelerWordTokenized={isDocumentSwaggerWordTokenized(
                            textSummarizationDetectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                        )}
                    />
                ) : (
                    <VisualizedConversationSummarizationResult
                        conversationSummarizationResult={conversationSummarization}
                        detectedLanguage={conversationSummarizationDetectedLanguages}
                        conversationItems={conversationSummarization && getConversationItems(document, 'conversationId')}
                        isRTL={isDocumentSwaggerRtl(
                            conversationSummarizationDetectedLanguages?.documents[0].detectedLanguage.iso6391Name ?? language
                        )}
                    />
                )}
            </ResultsStep>

            <NextStepsStep
                featureName={featureName}
                sdkLink={summarizationSdkLink}
                isResultsEmpty={!summarization}
                documentationLink={summarizationDocLink}
                sampleCodeLink={summarizationSamplesLink}
                demosToDisplay={['ner', 'keyPhrases', 'taForHealth']}
                curlCommand={
                    selectedPivot === 'conversation'
                        ? getConversationCurlCommand({
                              language,
                              languageAutoDetection,
                              conversation: document,
                              tasks: getConversationSummarizationPayloadTasks(selectedSummaryAspects, sentenceCount)
                          })
                        : getSummarizationCurlCommand({
                              language,
                              text: document,
                              languageAutoDetection,
                              endpoint: `ExtractiveSummarization`,
                            ...(sentenceCount && { sentenceCount })
                          })
                }
            />
        </Stack>
    );
});
